import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import BookPage from './BookPage';
import { Recipe } from '../models/Recipe';

interface RecipeRightPageProps {
  recipe: Recipe;
  onEdit: () => void;
  onDelete: () => void;
  pageNumber?: number;
}

const RecipeRightPage: React.FC<RecipeRightPageProps> = ({
  recipe,
  onEdit,
  onDelete,
  pageNumber,
}) => (
  <BookPage isLeftPage={false} pageNumber={pageNumber || (recipe.id ? parseInt(recipe.id.slice(-2), 16) % 100 + 11 : 11)}>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Action Buttons - Labeled for Clarity */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
        <Button 
          onClick={onEdit} 
          startIcon={<EditIcon />}
          sx={{ 
            color: 'text.primary',
            borderColor: 'text.primary',
            '&:hover': {
              backgroundColor: 'rgba(139, 69, 19, 0.1)',
            },
          }}
        >
          Edit Recipe
        </Button>
        <Button 
          onClick={onDelete} 
          startIcon={<DeleteIcon />}
          color="error"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
            },
          }}
        >
          Delete Recipe
        </Button>
      </Box>

      {/* Notes Section */}
      {recipe.notes && (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: '"Crimson Text", serif',
              fontWeight: 600,
              color: '#6D4C41',
              borderBottom: '2px solid rgba(139, 69, 19, 0.2)',
              pb: 1,
              mb: 2,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Notes
          </Typography>
          <Typography
            variant="body1" // Theme default (1.125rem)
            paragraph
            sx={{
              color: 'text.primary',
              pl: 2,
            }}
          >
            {recipe.notes}
          </Typography>
        </Box>
      )}

      {/* Chef's Tips (if available) */}
      {recipe.analysis?.chefTips && recipe.analysis.chefTips.length > 0 && (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: '"Crimson Text", serif',
              fontWeight: 600,
              color: '#6D4C41',
              borderBottom: '2px solid rgba(139, 69, 19, 0.2)',
              pb: 1,
              mb: 2,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Chef's Tips
          </Typography>
          <Box sx={{ pl: 2 }}>
            {recipe.analysis.chefTips.map((tip, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: '"Caveat", cursive',
                    fontSize: { xs: '20px', md: '24px' },
                    color: 'rgba(255, 193, 7, 0.8)',
                    mr: 1,
                  }}
                >
                  ✨
                </Typography>
                <Typography
                  variant="body1" // Theme default (1.125rem)
                  sx={{
                    color: 'text.primary',
                    fontStyle: 'italic',
                  }}
                >
                  {tip}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Recipe Info */}
      {recipe.analysis && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {recipe.analysis.difficulty.level && (
              <Chip
                label={`Difficulty: ${recipe.analysis.difficulty.level}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  color: '#6D4C41',
                  fontFamily: '"Crimson Text", serif',
                }}
              />
            )}
            {recipe.analysis.cuisine.primary !== 'other' && (
              <Chip
                label={`Cuisine: ${recipe.analysis.cuisine.primary}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  color: '#6D4C41',
                  fontFamily: '"Crimson Text", serif',
                  textTransform: 'capitalize',
                }}
              />
            )}
            {(() => {
              if (recipe.analysis.dietary.restrictions.length > 0) {
                return recipe.analysis.dietary.restrictions.map((restriction, index) => {
                  return (
                    <Chip
                      key={index}
                      label={restriction}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        color: '#2E7D32',
                        fontFamily: '"Crimson Text", serif',
                        textTransform: 'capitalize',
                      }}
                    />
                  );
                });
              } else {
                return null;
              }
            })()}
          </Box>
        </Box>
      )}

      {/* Decorative Divider */}
      <Box
        sx={{
          width: '100%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(139, 69, 19, 0.3), transparent)',
          my: 3,
        }}
      />
    </Box>
  </BookPage>
);

export default RecipeRightPage;
